// External-database sync API (/api/database-sync).
//
// Sync mappings tie a local transaction table to a table in an external
// application database (named connection from /api/project-db), pushing
// local changes out and/or pulling external changes in (bidirectional).

import apiClient from './client'

export interface SyncKeyPair { local: string; remote: string }
export interface SyncColumnPair { local: string; remote: string; transform?: string | null }

export interface SyncMapping {
  mapping_name: string
  connection_name: string
  direction: 'push' | 'pull' | 'bidirectional'
  local_table: string
  remote_schema?: string
  remote_table: string
  key_map: SyncKeyPair[]
  column_map: SyncColumnPair[]
  push?: { interval_seconds?: number; max_attempts?: number }
  pull?: {
    mode?: 'watermark' | 'diff'
    watermark_column?: string
    interval_seconds?: number
    delete_policy?: 'ignore' | 'key_diff'
  }
  conflict_policy?: 'last_writer_wins' | 'twasta_wins' | 'external_wins' | 'review'
  enabled?: boolean
  _issues?: string[]
}

export interface SyncMappingStatus {
  mapping_name: string
  connection_name: string
  direction: string
  local_table: string
  remote_table: string
  enabled: boolean
  issues: string[]
  worker_running: boolean
  last_push_at?: number | null
  last_push?: Record<string, number>
  last_pull_at?: number | null
  last_pull?: Record<string, unknown>
  last_error?: string | null
  outbox?: Record<string, number>
}

export interface SyncConflict {
  id: number | string
  mapping_name: string
  local_pk: string
  local_values: Record<string, unknown>
  remote_values: Record<string, unknown>
  status: string
}

export async function listSyncMappings(project: string): Promise<SyncMapping[]> {
  const res = await apiClient.get('/database-sync/mappings', { params: { project } })
  return res.data?.mappings ?? []
}

export async function saveSyncMapping(project: string, mapping: SyncMapping) {
  const res = await apiClient.post('/database-sync/mappings/save', { project, mapping })
  return res.data as { success: boolean; issues: string[] }
}

export async function deleteSyncMapping(project: string, mappingName: string) {
  const res = await apiClient.post('/database-sync/mappings/delete', null, {
    params: { project, mapping_name: mappingName },
  })
  return res.data
}

export async function introspectRemote(
  project: string, connectionName: string, table?: string, schema?: string,
) {
  const res = await apiClient.post('/database-sync/introspect', null, {
    params: { project, connection_name: connectionName, table, schema },
  })
  return res.data as {
    success: boolean
    message?: string
    tables?: string[]
    columns?: Array<{ name: string; type: string }>
  }
}

export async function syncNow(project: string) {
  const res = await apiClient.post('/database-sync/sync-now', null, { params: { project } })
  return res.data
}

export async function getSyncStatus(project: string): Promise<SyncMappingStatus[]> {
  const res = await apiClient.get('/database-sync/status', { params: { project } })
  return res.data?.mappings ?? []
}

export async function listSyncConflicts(project: string): Promise<SyncConflict[]> {
  const res = await apiClient.get('/database-sync/conflicts', { params: { project } })
  return res.data?.conflicts ?? []
}

export async function resolveSyncConflict(
  project: string, conflictId: number | string, keep: 'local' | 'remote',
) {
  const res = await apiClient.post('/database-sync/conflicts/resolve', {
    project, conflict_id: conflictId, keep,
  })
  return res.data
}

export async function retrySyncOutbox(project: string, ids?: Array<number | string>) {
  const res = await apiClient.post('/database-sync/outbox/retry', { project, ids })
  return res.data as { success: boolean; retried: number }
}
